import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_TAMPERING } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  AntimalwareAntiTamperingRebootTimes,
  AntimalwareAntiTamperingVulnerableDriversAction,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  AntimalwareAntiTamperingFormValue,
  AntimalwareAntiTamperingSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import {
  ANTIMALWARE_ANTI_TAMPERING_DEFAULT_VALUE,
  getAntimalwareAntiTamperingSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_TAMPERING)
export class GzAntimalwareAntiTamperingWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareAntiTamperingFormValue,
  AntimalwareAntiTamperingSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.ANTIMALWARE_ANTI_TAMPERING,
  };
  protected searchableKeywords = getAntimalwareAntiTamperingSearchableKeywords();

  @query('#antimalware-anti-tampering-toggle')
  private antiTamperingEnableField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#antimalware-anti-tampering-info-text')
  private antiTamperingInfoText: GzTextWebComponent;
  @query('#antimalware-anti-tampering-content')
  private antiTamperingContent: HTMLElement;
  @query('#vulnerable-drivers-checkbox-form')
  private vulnerableDriversEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#vulnerable-drivers-radio-group-form')
  private vulnerableDriversActionField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#callback-evasion-checkbox-form')
  private callbackEvasionEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#callback-evasion-actions-section')
  private callbackEvasionContent: HTMLElement;
  @query('#callback-evasion-actions-isolate-endpoint-checkbox-form')
  private callbackEvasionIsolateEndpointField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#callback-evasion-actions-reboot-endpoint-checkbox-form')
  private callbackEvasionRebootEndpointField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#callback-evasion-actions-radio-group-form')
  private callbackEvasionRebootTimeField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="antimalware-anti-tampering-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.AntiTampering].label}"
        gz-form-input
        @toggleChange="${e => {
          toggleDisabled(this.antiTamperingInfoText, !e.detail.checked);
          toggleDisabled(this.antiTamperingContent, !e.detail.checked);
        }}"
      >
        <span
          >${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.AntiTampering].translatedLabel}</span
        >
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text
      id="antimalware-anti-tampering-info-text"
      fontSize="${FontSizes.PARAGRAPH_12}"
      fontWeight="${FontWeights.NORMAL}"
      class="infoText"
      >${translateFn(
        "Anti-tampering helps you identify attempts to disable the security agent or compromise vulnerable drivers, safeguarding your endpoint's security integrity."
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="antimalware-anti-tampering-content">
        <gz-title
          fontWeight="${FontWeights.SEMIBOLD}"
          fontSize="${FontSizes.PARAGRAPH_12}"
          class="mt-4 subtitle"
          searchable-label="${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.PreTampering].label}"
          >${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.PreTampering].translatedLabel}</gz-title
        >
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mt-1"
          >${translateFn(
            "Detects vulnerable drivers on endpoint that can be used to threaten the security agent's integrity. Compatible with Windows and Linux operating systems."
          )}
        </gz-text>

        <div id="vulnerable-drivers-section" class="mt-2">
          <!-- vulnerable drivers checkbox form -->
          <gz-form-field id="vulnerable-drivers-checkbox-form">
            <gz-checkbox
              gz-form-input
              id="vulnerable-drivers-checkbox"
              @change="${e => toggleDisabled(this.vulnerableDriversActionField, !e.detail.checked)}"
              searchable-label="${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.VulnerableDrivers]
                .label}"
            >
              ${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.VulnerableDrivers]
                .translatedLabel}</gz-checkbox
            >
          </gz-form-field>

          <!-- vulnerable drivers radio group form -->
          <gz-form-field id="vulnerable-drivers-radio-group-form">
            <gz-radio-group gz-form-input id="vulnerable-drivers-radio-group">
              <gz-radio
                label="${translateFn('Deny access')}"
                .value="${AntimalwareAntiTamperingVulnerableDriversAction.DENY_ACCESS}"
                id="vulnerable-drivers-action-deny-access"
              ></gz-radio>
              <gz-radio
                label="${translateFn('Disinfect')}"
                .value="${AntimalwareAntiTamperingVulnerableDriversAction.DISINFECT}"
                id="vulnerable-drivers-action-disinfect"
              ></gz-radio>
              <gz-radio
                label="${translateFn('Report only')}"
                .value="${AntimalwareAntiTamperingVulnerableDriversAction.REPORT_ONLY}"
                id="vulnerable-drivers-action-report-only"
              ></gz-radio>
            </gz-radio-group>
          </gz-form-field>
        </div>

        <gz-title
          fontWeight="${FontWeights.SEMIBOLD}"
          fontSize="${FontSizes.PARAGRAPH_12}"
          class="mt-4 subtitle"
          searchable-label="${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.PostTampering].label}"
          >${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.PostTampering]
            .translatedLabel}</gz-title
        >
        <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText mt-1"
          >${translateFn(
            "Detects whether the security agent's callbacks have been maliciously removed or disabled on endpoint, resulting in compromised integrity. Compatible with Windows operating systems."
          )}
        </gz-text>

        <div id="callback-evasion-section" class="mt-2">
          <!-- callback evasion checkbox form -->
          <gz-form-field id="callback-evasion-checkbox-form">
            <gz-checkbox
              gz-form-input
              id="callback-evasion-checkbox"
              searchable-label="${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.CallbackEvasion]
                .label}"
              @change="${e => toggleDisabled(this.callbackEvasionContent, !e.detail.checked)}"
              >${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.CallbackEvasion]
                .translatedLabel}</gz-checkbox
            >
          </gz-form-field>

          <!-- callback evasion actions section-->
          <div id="callback-evasion-actions-section">
            <!-- isolate endpoint checkbox -->
            <gz-form-field class="pb-1" id="callback-evasion-actions-isolate-endpoint-checkbox-form">
              <gz-checkbox
                gz-form-input
                id="callback-evasion-actions-isolate-endpoint-checkbox"
                searchable-label="${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.IsolateEndpoint]
                  .label}"
                >${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.IsolateEndpoint]
                  .translatedLabel}</gz-checkbox
              >
            </gz-form-field>

            <!-- reboot endpoint checkbox -->
            <gz-form-field id="callback-evasion-actions-reboot-endpoint-checkbox-form">
              <gz-checkbox
                gz-form-input
                id="callback-evasion-actions-reboot-endpoint-checkbox"
                searchable-label="${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.RebootEndpoint]
                  .label}"
                @change="${e => toggleDisabled(this.callbackEvasionRebootTimeField, !e.detail.checked)}"
                >${this.searchableKeywords[AntimalwareAntiTamperingSearchableKeywords.RebootEndpoint]
                  .translatedLabel}</gz-checkbox
              >
            </gz-form-field>

            <!-- time options for reboot endpoint -->
            <gz-form-field id="callback-evasion-actions-radio-group-form">
              <gz-radio-group gz-form-input id="callback-evasion-actions-radio-group">
                <gz-radio
                  label="${translateFn('Immediately')}"
                  .value="${AntimalwareAntiTamperingRebootTimes.IMMEDIATELY}"
                  id="callback-evasion-action-immediately"
                ></gz-radio>
                <gz-radio
                  label="${translateFn('5 min')}"
                  .value="${AntimalwareAntiTamperingRebootTimes.MIN_5}"
                  id="callback-evasion-action-5-min"
                ></gz-radio>
                <gz-radio
                  label="${translateFn('10 min')}"
                  .value="${AntimalwareAntiTamperingRebootTimes.MIN_10}"
                  id="callback-evasion-action-10-min"
                ></gz-radio>
                <gz-radio
                  label="${translateFn('15 min')}"
                  .value="${AntimalwareAntiTamperingRebootTimes.MIN_15}"
                  id="callback-evasion-action-15-min"
                ></gz-radio>
              </gz-radio-group>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.antiTampering
   */
  protected getValueFromManager(): AntimalwareAntiTamperingFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: { [SectionId.ANTIMALWARE_ANTI_TAMPERING]: ANTIMALWARE_ANTI_TAMPERING_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_ANTI_TAMPERING]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.antimalware.antiTampering'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          antiTampering: {
            enabled: this.antiTamperingEnableField,
            vulnerableDrivers: {
              enabled: this.vulnerableDriversEnableField,
              action: this.vulnerableDriversActionField,
            },
            callbackEvasion: {
              enabled: this.callbackEvasionEnableField,
              actions: {
                isolateEndpoint: this.callbackEvasionIsolateEndpointField,
                rebootEndpoint: this.callbackEvasionRebootEndpointField,
                rebootTime: this.callbackEvasionRebootTimeField,
              },
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          antiTampering: this.valueT.uiSettings.antimalware.antiTampering,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'antiTampering'], {});
    }

    return value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_TAMPERING: GzAntimalwareAntiTamperingWebComponent;
  }
}
